import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Input, Output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { selectChatState, selectUiState } from '../../../../store/app.selectors';

import { MessagingEnvironmentService } from '../../../../core/services/messaging-environment.service';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { uiInitialState } from '../../../shared/store/ui.reducer';
import {
  setBackArrow,
  setChannelsLoaded,
  setLoadingChannel,
  setLoadingChannels,
  setShowChannel,
  setShowChannels,
  setShowChannelsFilters,
  setShowGoToOrderButton,
  setShowProfile,
  setShowSidebar,
} from '../../../shared/store/ui.actions';
import { AuthenticationService } from '../../../user/services/authentication.service';
import { ChannelServiceType, ChannelStatus } from '../../interfaces/IChannel';
import { IDealerSetting } from '../../interfaces/IDealerSettings';
import { WorkspaceShorter } from '../../interfaces/IWorkspace';
import { PhonesService } from '../../services/phones.service';
import { MessagingStateService } from '../../services/state.service';
import { MessagingTranslationService } from '../../services/translations.service';
import { WorkspacesService } from '../../services/workspaces.service';
import {
  loadChannels,
  setAuthorizationTokenKey,
  setFilterValue,
  setInputPhoneNumber,
  setSearchTerm,
} from '../../store/chat.actions';
import { AppStateWithChat, chatInitialState } from '../../store/chat.reducer';

type FiltersParamsTypes = 'owns' | 'unreadMessages' | 'status';

@Component({
  selector: 'ngx-messaging-layout',
  templateUrl: './ngx-messaging-layout.component.html',
  styleUrls: ['./ngx-messaging-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MessagingLayoutComponent extends BaseComponent {
  @Input() hideSideBar: boolean;
  @Input() loadSavedState: string;
  @Input() showOnlyList: boolean;
  @Input() workspace: WorkspaceShorter;
  @Input() dealerSettings: IDealerSetting[];
  @Input() channelID: string;
  @Input() phoneNumber: string;
  @Input() subAccountUID: string;
  @Input() routing: ChannelServiceType;
  @Input() status: ChannelStatus;
  @Input() unreadMessages: boolean;
  @Input() tokenAuthorization: string;
  @Input('owner') owns: boolean;
  @Output() orderIdEvent = new EventEmitter<string>();
  @Output() settingsEvent = new EventEmitter<boolean>();

  private store = inject(Store<AppStateWithChat>);
  public translationService = inject(MessagingTranslationService);
  public env = inject(MessagingEnvironmentService);
  private authService = inject(AuthenticationService);
  private workspaceService = inject(WorkspacesService);
  private stateService = inject(MessagingStateService);
  private phoneService = inject(PhonesService);

  ui = toSignal(this.store.select(selectUiState), { initialValue: uiInitialState });
  chat = toSignal(this.store.select(selectChatState), { initialValue: chatInitialState });
  filters = computed(() => this.chat().channelsFilter.filters);
  isChannelsLoaded = computed(() => this.ui().isChannelsLoaded);

  ngOnInit(): void {
    if (this.tokenAuthorization) {
      this.store.dispatch(setAuthorizationTokenKey({ key: this.tokenAuthorization }));
    }
    this.store.dispatch(setInputPhoneNumber({ phoneNumber: null }));
    if (!this.stateService.hasSavedState(this.loadSavedState)) {
      this.loadApp();
    } else {
      this.stateService.loadState(this.loadSavedState);
    }
  }

  async loadApp(): Promise<void> {
    this.store.dispatch(setLoadingChannels({ value: true }));
    this.store.dispatch(setLoadingChannel({ value: true }));
    this.workspaceService.loadWorkspaces();
    this.workspaceService.selectWorkspace(this.workspace);
    this.store.dispatch(setChannelsLoaded({ value: false }));
    this.store.dispatch(setShowChannels({ value: true }));
    await this.authService.loadProfile({ dealerSettings: this.dealerSettings || [], subAccountUID: this.subAccountUID });
    this.phoneNumber = this.phoneService.setPhoneNumber(this.phoneNumber);
    this.store.dispatch(setShowProfile({ value: true }));
    this.store.dispatch(setShowGoToOrderButton({ value: true }));
    this.store.dispatch(setShowSidebar({ value: !this.hideSideBar }));
    this.checkIsOnlyList();
    this.checkFilters();
    this.loadChannels();
  }

  checkFilters(): void {
    if (this.channelID || this.phoneNumber) {
      this.disableFilters();
      if (this.phoneNumber) {
        this.store.dispatch(setSearchTerm({ searchTerm: this.phoneNumber }));
      }
      return;
    }
    const filtersParams = {
      owns: 'owns',
      unreadMessages: 'unreadMessages',
      status: 'status',
    };
    for (const key in filtersParams) {
      if (this[key as FiltersParamsTypes] !== null && this[key as FiltersParamsTypes] !== undefined) {
        this.store.dispatch(setShowChannelsFilters({ value: true }));
        const filter = this.filters()?.find(
          (e) => e.field?.toUpperCase() === key.toUpperCase() || e.name.toUpperCase() === key.toUpperCase()
        );
        if (filter) {
          if (filter.name.toUpperCase() === 'STATUS' && this.status === 'OPEN') {
            this.status = ChannelStatus.ACTIVE;
          }
          this.store.dispatch(setFilterValue({ value: this[key as FiltersParamsTypes], key }));
          if (filter?.disableOthers?.length) {
            for (const otherFilter of filter.disableOthers) {
              this.store.dispatch(setFilterValue({ value: false, key: otherFilter }));
            }
          }
        }
      }
    }
  }

  disableFilters(): void {
    for (const filter of this.filters() ?? []) {
      this.store.dispatch(setFilterValue({ value: null, key: filter.name }));
    }
  }

  loadChannels(): void {
    if (!this.isChannelsLoaded()) {
      this.store.dispatch(
        loadChannels({
          autoSelectChannel: true,
          channelID: this.channelID || null,
          phoneNumber: this.phoneNumber,
          routing: this.routing,
          setLoading: true,
          concat: false,
        })
      );
    } else {
      this.store.dispatch(setLoadingChannels({ value: false }));
      this.store.dispatch(setLoadingChannel({ value: false }));
    }
  }

  checkIsOnlyList(): void {
    if (this.showOnlyList) {
      this.store.dispatch(setShowSidebar({ value: false }));
      this.store.dispatch(setShowProfile({ value: false }));
      this.store.dispatch(setShowChannel({ value: false }));
      this.store.dispatch(setBackArrow({ value: true }));
    }
  }

  emitOrderIdEvent(event: string): void {
    this.orderIdEvent.emit(event);
  }

  goSettings(): void {
    this.settingsEvent.emit(true);
  }
}
